import {Label} from '@/components/ui/label/Label'
import {ComboboxProvider, useComboboxStore} from '@ariakit/react'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useState} from 'react'
import {debounce} from '@/utilities/helpers'
import useAddressGeocoding from '@/features/users/hooks/useAddressGeocoding'
import {useAsync} from '@/hooks/useAsync'
import {ComboboxInput} from '@/components/ui/combobox-atoms/ComboboxAtoms'
import {useTranslation} from '@/translations/i18n'
import {SearchSmIcon} from '@/components/ui/icon/Icon'
import {InputAddressAutocompletePopover} from '@/components/commons/input-address-autocomplete-popover/InputAddressAutocompletePopover'
import {Coordinates} from '@/types'

type PredictionType = 'geocode' | 'establishment' | 'address' | `(regions)` | `(cities)`

interface InputAddressAutocompleteProps {
    label: string
    onChange: (value: google.maps.GeocoderResult | null) => void
    onLatLngFound: (value: Coordinates | null) => void
    placeholder?: string
    country?: string
    province?: string
    errorMessage?: string
    showManualForm: () => void
    predictionTypes: PredictionType[]
}

export const InputAddressAutocomplete = ({
    label,
    onChange,
    onLatLngFound,
    placeholder,
    errorMessage,
    showManualForm,
    predictionTypes
}: InputAddressAutocompleteProps) => {
    const {t} = useTranslation()
    const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[] | null>(null)
    const searchPlace = useAsync()
    const store = useComboboxStore()

    const searchPlaces = debounce(async (value: string) => {
        if (!value) {
            setPredictions(null)
            return
        }

        try {
            const autocomplete = new google.maps.places.AutocompleteService()
            const result = await searchPlace.run(
                autocomplete.getPlacePredictions({
                    input: value,
                    componentRestrictions: {country: 'it'},
                    types: predictionTypes
                })
            )
            setPredictions(result?.predictions ?? null)
        } catch (err) {
            console.error(err)
        }
    }, 750)

    //Geocoder's logic and utils
    const {retrieveLatLng, retrieveStructured} = useAddressGeocoding({
        onLatLngFound,
        onStructuredAddressFound: onChange
    })

    // On select value retrieve all the geocoding data
    const handleSelectAddress = async (placeId: string) => {
        if (!predictions) return
        const address = predictions.find(prediction => prediction.place_id == placeId)?.description
        if (address) {
            await retrieveStructured(placeId)
            await retrieveLatLng(address)
        }
    }

    return (
        <ComboboxProvider
            store={store}
            setValue={searchPlaces}
            setSelectedValue={placeId => handleSelectAddress(placeId as string)}
            resetValueOnHide
            placement="bottom"
        >
            <Flexbox direction="column" gap={1.5} fullWidth>
                <Label>{label}</Label>
                <ComboboxInput
                    typeIcon={<SearchSmIcon size={14} />}
                    hasCloseCombobox
                    errorMessage={errorMessage}
                    placeholder={placeholder || t('commons:search_address_placeholder')}
                />
                <InputAddressAutocompletePopover
                    predictions={predictions}
                    searchPlace={searchPlace}
                    showManualForm={() => {
                        showManualForm()
                        store.setOpen(false)
                    }}
                />
                {!!errorMessage && (
                    <Flexbox align="center" gap={1.5}>
                        <InputStatusIcon hasError={true} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>
                )}
            </Flexbox>
        </ComboboxProvider>
    )
}
